import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock } from '@fortawesome/free-solid-svg-icons'
import type { PickerProps } from 'antd/es/date-picker/generatePicker'
import moment, { Moment } from 'moment-timezone'
import React from 'react'
import { DatePicker } from '../DatePicker'
import './styles.scss'

export type TimePickerProps = Omit<PickerProps<Moment>, 'picker'>

const clsPrefix = 'time-picker'

export const TimePicker = React.forwardRef<any, TimePickerProps>((props, ref) => (
  <DatePicker
    ref={ref}
    picker="time"
    mode={undefined}
    data-testid="time-picker"
    aria-label="Pick a time"
    style={{ width: '100%' }}
    className={clsPrefix}
    popupClassName={`${clsPrefix}-popup`}
    minuteStep={15}
    use12Hours
    suffixIcon={<FontAwesomeIcon icon={faClock} />}
    {...props}
  />
))
TimePicker.displayName = 'TimePicker'

export interface FormTimePickerProps extends Omit<TimePickerProps, 'onChange'> {
  // Time format to return in onChange event, following moment formatting options
  timeFormat?: string
  // Time string in format Hmm -> 1700 for example
  onChange?: (timeString: string | null) => void
}

const displayFormat = 'h:mm A'

/**
 * This component is very similar to the normal time picker. The main difference
 * is that it calls onChange when time picker triggers onSelect and onBlur.
 * The reason behind this is that when the user enters a value using the keyboard
 * or just click away (onBlur) we want to trigger a change on the Form component.
 *
 * Normally we would be able to specify to antd Form Item to update onChange
 * AND onSelect, but since the onBlur event interests us in this specific case
 * we implemented this wrapper component
 *
 * @param props - See type {@link FormTimePickerProps}
 */
export const FormTimePicker = React.forwardRef<any, FormTimePickerProps>(
  ({ value, timeFormat = 'Hmm', onChange, ...rest }, ref) => {
    const handleOnChange = (time: Moment | null) => {
      if (!time || !time.isValid()) {
        onChange && onChange(null)
        return
      }
      onChange && onChange(time.format(timeFormat))
    }

    return (
      <TimePicker
        ref={ref}
        // @ts-expect-error -- Changed by antd ?
        value={value ? moment(value, timeFormat) : undefined}
        onSelect={handleOnChange}
        // @ts-expect-error -- Changed by antd ?
        onChange={handleOnChange}
        format={displayFormat}
        needConfirm={false}
        {...rest}
      />
    )
  }
)
FormTimePicker.displayName = 'FormTimePicker'

export default TimePicker
